import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import ConfirmModal from './ConfirmModal';
import LoadingSpinner from './LoadingSpinner';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  category: string;
  unit: string;
}

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState<number | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    category: '',
    unit: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/');
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'price' ? Number.parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, formData);
      } else {
        await api.post('/products/', formData);
      }
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Erreur lors de l\'enregistrement du produit');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      price: product.price,
      category: product.category,
      unit: product.unit
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    setProductToDelete(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    
    try {
      await api.delete(`/products/${productToDelete}`);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Erreur lors de la suppression du produit');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      category: '',
      unit: ''
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (loading) {
    return <LoadingSpinner message="Chargement des produits..." />;
  }

  return (
    <div className="products-container">
      <div className="page-header">
        <h2>📦 Gestion des Produits</h2>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          ➕ Nouveau Produit
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="🔍 Rechercher par nom ou catégorie..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredProducts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <p className="empty-message">
            {searchTerm ? 'Aucun produit ne correspond à votre recherche' : 'Aucun produit disponible'}
          </p>
          {!searchTerm && (
            <button className="btn-primary" onClick={() => setShowModal(true)}>
              Créer votre premier produit
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop: Table View */}
          <div className="products-table-view">
            <div className="table-container">
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Description</th>
                    <th>Prix</th>
                    <th>Catégorie</th>
                    <th>Unité</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(product => (
                    <tr key={product.id}>
                      <td>
                        <div className="product-name-cell">
                          <span className="product-id">#{product.id}</span>
                          <strong>{product.name}</strong>
                        </div>
                      </td>
                      <td className="description-cell">{product.description || '-'}</td>
                      <td><strong className="price-value">{product.price.toFixed(2)} CFA</strong></td>
                      <td><span className="category-badge">{product.category}</span></td>
                      <td><span className="unit-label">{product.unit}</span></td>
                      <td>
                        <div className="actions-cell">
                          <button 
                            className="btn-icon btn-edit" 
                            onClick={() => handleEdit(product)}
                            title="Modifier"
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn-icon btn-delete" 
                            onClick={() => handleDelete(product.id)}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile: Cards View */}
          <div className="products-cards-view">
            {filteredProducts.map(product => (
              <div key={product.id} className="product-card">
                <div className="product-card-header">
                  <div className="product-title-section">
                    <span className="product-id-badge">#{product.id}</span>
                    <h3 className="product-title">{product.name}</h3>
                  </div>
                  <span className="category-badge">{product.category}</span>
                </div>
                
                <div className="product-card-body">
                  {product.description && (
                    <p className="product-description">{product.description}</p>
                  )}
                  
                  <div className="product-info-grid">
                    <div className="product-info-item">
                      <span className="info-label">Prix</span>
                      <span className="info-value price-value">{product.price.toFixed(2)} CFA</span>
                    </div>
                    <div className="product-info-item">
                      <span className="info-label">Unité</span>
                      <span className="info-value">{product.unit}</span>
                    </div>
                  </div>
                </div>
                
                <div className="product-card-actions">
                  <button 
                    className="btn-card btn-edit" 
                    onClick={() => handleEdit(product)}
                  >
                    <span className="btn-icon">✏️</span>
                    <span className="btn-text">Modifier</span>
                  </button>
                  <button 
                    className="btn-card btn-delete" 
                    onClick={() => handleDelete(product.id)}
                  >
                    <span className="btn-icon">🗑️</span>
                    <span className="btn-text">Supprimer</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProduct ? 'Modifier le Produit' : 'Nouveau Produit'}</h3>
              <button className="btn-close" onClick={handleCloseModal}>✕</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nom du Produit *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Ex: Savon liquide"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Description du produit"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price">Prix (CFA) *</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="unit">Unité *</label>
                  <select
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sélectionner...</option>
                    <option value="pièce">Pièce</option>
                    <option value="kg">Kilogramme (kg)</option>
                    <option value="litre">Litre (L)</option>
                    <option value="mètre">Mètre (m)</option>
                    <option value="paquet">Paquet</option>
                    <option value="boîte">Boîte</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="category">Catégorie *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Sélectionner une catégorie...</option>
                  <option value="Alimentaire">Alimentaire</option>
                  <option value="Hygiène">Hygiène</option>
                  <option value="Cosmétique">Cosmétique</option>
                  <option value="Électronique">Électronique</option>
                  <option value="Vêtements">Vêtements</option>
                  <option value="Maison">Maison</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingProduct ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirm}
        title="Supprimer le produit"
        message="Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirm(false);
          setProductToDelete(null);
        }}
      />
    </div>
  );
};

export default Products;